import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { toDateTimeString } from "../../date_utils";
import { useToast } from "../../hooks/useToast";
import {
    ATTACHMENT_ACCEPT,
    ATTACHMENT_MAX_BYTES,
    attachmentCategoryLabel,
    attachmentCategoryOptions,
    deleteEquipmentAttachment,
    downloadEquipmentAttachment,
    formatFileSize,
    listEquipmentAttachments,
    uploadEquipmentAttachment,
} from "../../services/equipment_service";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
    },
    bar: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "end",
        ...FC.shorthands.gap("10px"),
        ...FC.shorthands.padding("12px"),
        backgroundColor: FC.tokens.colorNeutralBackground2,
        ...FC.shorthands.borderRadius(FC.tokens.borderRadiusMedium),
    },
    field: {
        display: "flex",
        minWidth: "150px",
        flexDirection: "column",
        ...FC.shorthands.gap("4px"),
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "13px",
    },
    cell: {
        textAlign: "left",
        ...FC.shorthands.padding("6px"),
        borderBottom: `1px solid ${FC.tokens.colorNeutralStroke2}`,
    },
    muted: {
        color: FC.tokens.colorNeutralForeground3,
    },
    pager: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        ...FC.shorthands.gap("8px"),
    },
});

const errorText = (error) => {
    const trace = error?.traceId ? `（追踪号 ${error.traceId}）` : "";
    return `${error?.message || "操作失败"}${trace}`;
};

/**
 * Files attached to an equipment record, or to one of its work orders when `serviceRecordId`
 * is given.
 *
 * Uploads are checked again by the server against the file's magic number, so the picker filter
 * and the size check here are conveniences that keep the user from waiting on a doomed upload —
 * not controls. Downloads stream through the API and are always saved, never opened inline.
 */
export const AttachmentPanel = ({ equipmentId, serviceRecordId = null, canWrite = false }) => {
    const styles = useStyles();
    const showToast = useToast();
    const fileInputRef = useRef(null);

    const [page, setPage] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [category, setCategory] = useState("photo");
    const [uploading, setUploading] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Monotonic request id: a slow older response must never overwrite a newer one. Without it,
    // switching equipment or refreshing after an upload can leave the previous unit's files on
    // screen, because the earlier GET can land last.
    const requestRef = useRef(0);

    const load = useCallback(
        async (targetPage) => {
            if (!equipmentId) {
                return;
            }
            const seq = ++requestRef.current;
            setLoading(true);
            setLoadError("");
            try {
                const result = await listEquipmentAttachments(equipmentId, {
                    serviceRecordId,
                    page: targetPage,
                });
                if (seq === requestRef.current) {
                    setPage(result);
                }
            } catch (error) {
                console.error("Failed to load attachments:", error);
                if (seq === requestRef.current) {
                    // An error is not "no files": the operator would conclude none were ever uploaded.
                    setPage(null);
                    setLoadError(errorText(error));
                }
            } finally {
                if (seq === requestRef.current) {
                    setLoading(false);
                }
            }
        },
        [equipmentId, serviceRecordId]
    );

    useEffect(() => {
        load(pageNumber);
    }, [load, pageNumber]);

    const reload = useCallback(() => load(pageNumber), [load, pageNumber]);

    const items = page?.items || [];
    const totalPages = page?.totalPages || 0;
    const currentPage = page?.page || pageNumber;

    const onPick = async (event) => {
        const file = event.target.files?.[0];
        // Reset immediately so picking the same file twice still fires a change event.
        event.target.value = "";
        if (!file) {
            return;
        }

        if (file.size > ATTACHMENT_MAX_BYTES) {
            showToast(
                "文件过大",
                "error",
                `单个附件不能超过 ${ATTACHMENT_MAX_BYTES / (1024 * 1024)} MiB，当前 ${formatFileSize(file.size)}。`
            );
            return;
        }

        setUploading(true);
        try {
            await uploadEquipmentAttachment(equipmentId, { file, category, serviceRecordId });
            showToast("附件已上传", "success");
            // Newest first, so a fresh upload is on page one.
            setPageNumber(1);
            reload();
        } catch (error) {
            console.error("Failed to upload attachment:", error);
            showToast("上传失败", "error", errorText(error));
        } finally {
            setUploading(false);
        }
    };

    const onDownload = async (attachment) => {
        try {
            await downloadEquipmentAttachment(equipmentId, attachment);
        } catch (error) {
            console.error("Failed to download attachment:", error);
            showToast("下载失败", "error", errorText(error));
        }
    };

    const onDelete = async () => {
        const target = deleteTarget;
        if (!target) {
            return;
        }
        try {
            await deleteEquipmentAttachment(equipmentId, target.id);
            showToast("附件已删除", "success");
            setDeleteTarget(null);

            // Removing the only row on the last page leaves that page past the end. The server
            // reports the request faithfully — Page=2, TotalPages=1, no items — so the screen would
            // read "第 2 / 1 页，暂无附件" while page one still holds a full set. Step back instead.
            if (items.length === 1 && currentPage > 1) {
                setPageNumber(currentPage - 1);
            } else {
                reload();
            }
        } catch (error) {
            console.error("Failed to delete attachment:", error);
            showToast("删除失败", "error", errorText(error));
        }
    };

    return (
        <div className={styles.root}>
            {canWrite && (
                <div className={styles.bar}>
                    <div className={styles.field}>
                        <FC.Label>附件分类</FC.Label>
                        <FC.Dropdown
                            selectedOptions={[category]}
                            value={attachmentCategoryLabel(category)}
                            onOptionSelect={(_, data) => setCategory(data.optionValue)}
                        >
                            {attachmentCategoryOptions.map((option) => (
                                <FC.Option key={option.value} value={option.value}>
                                    {option.label}
                                </FC.Option>
                            ))}
                        </FC.Dropdown>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ATTACHMENT_ACCEPT}
                        style={{ display: "none" }}
                        onChange={onPick}
                    />
                    <FC.Button
                        appearance="primary"
                        icon={<IC.ArrowUploadRegular />}
                        disabled={uploading}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {uploading ? "上传中..." : "上传附件"}
                    </FC.Button>
                    <span className={styles.muted}>
                        {`支持 JPEG、PNG、WebP、PDF、TXT，单个不超过 ${
                            ATTACHMENT_MAX_BYTES / (1024 * 1024)
                        } MiB。Office 文件请先转换为 PDF。`}
                    </span>
                </div>
            )}

            {loadError && (
                <FC.MessageBar intent="error">
                    <FC.MessageBarBody>{loadError}</FC.MessageBarBody>
                </FC.MessageBar>
            )}

            <table className={styles.table}>
                <thead>
                    <tr>
                        {["文件名", "分类", "大小", "上传人", "上传时间", "操作"].map((label) => (
                            <th className={styles.cell} key={label}>
                                {label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {items.map((row) => (
                        <tr key={row.id}>
                            <td className={styles.cell}>{row.originalFileName}</td>
                            <td className={styles.cell}>{attachmentCategoryLabel(row.category)}</td>
                            <td className={styles.cell}>{formatFileSize(row.fileSizeBytes)}</td>
                            <td className={styles.cell}>{row.uploadedByName || row.uploadedBy}</td>
                            <td className={styles.cell}>{toDateTimeString(row.createdAtUtc)}</td>
                            <td className={styles.cell}>
                                <FC.Button
                                    appearance="subtle"
                                    icon={<IC.ArrowDownloadRegular />}
                                    onClick={() => onDownload(row)}
                                >
                                    下载
                                </FC.Button>
                                {/* canDelete comes from the server: Admin, or whoever uploaded it. */}
                                {row.canDelete && (
                                    <FC.Button
                                        appearance="subtle"
                                        icon={<IC.DeleteRegular />}
                                        onClick={() => setDeleteTarget(row)}
                                    >
                                        删除
                                    </FC.Button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {!loading && items.length === 0 && !loadError && <div className={styles.muted}>暂无附件</div>}
            {loading && <FC.Spinner size="tiny" label="加载中..." />}

            <div className={styles.pager}>
                <span className={styles.muted}>
                    {`共 ${page?.totalCount || 0} 个附件，第 ${currentPage} / ${Math.max(totalPages, 1)} 页`}
                </span>
                <FC.Button
                    disabled={loading || currentPage <= 1}
                    onClick={() => setPageNumber(currentPage - 1)}
                >
                    上一页
                </FC.Button>
                <FC.Button
                    disabled={loading || currentPage >= totalPages}
                    onClick={() => setPageNumber(currentPage + 1)}
                >
                    下一页
                </FC.Button>
            </div>

            <FC.Dialog
                open={Boolean(deleteTarget)}
                onOpenChange={(_, data) => {
                    if (!data.open) {
                        setDeleteTarget(null);
                    }
                }}
            >
                <FC.DialogSurface>
                    <FC.DialogBody>
                        <FC.DialogTitle>删除附件</FC.DialogTitle>
                        <FC.DialogContent>
                            {`确认删除「${deleteTarget?.originalFileName || ""}」？删除会记入操作审计，且不可撤销。`}
                        </FC.DialogContent>
                        <FC.DialogActions>
                            <FC.Button appearance="secondary" onClick={() => setDeleteTarget(null)}>
                                取消
                            </FC.Button>
                            <FC.Button appearance="primary" onClick={onDelete}>
                                删除
                            </FC.Button>
                        </FC.DialogActions>
                    </FC.DialogBody>
                </FC.DialogSurface>
            </FC.Dialog>
        </div>
    );
};
